import { Pin } from "lucide-react";
import { TagBadge } from "@/components/ui/TagBadge";
import type { NoteListItem } from "../types";

type Props = {
  note: NoteListItem;
  onClick: () => void;
  onTogglePin: () => void;
  tagLabels?: Record<string, string>;
  typeLabels?: Record<string, string>;
};

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  general: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  meeting_minutes: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  technical_doc: { bg: "hsl(270,70%,96%)", text: "hsl(270,60%,42%)" },
  decision_log: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  reference: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  personal: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

export function NoteCard({
  note,
  onClick,
  onTogglePin,
  tagLabels = {},
  typeLabels = {},
}: Props) {
  const contextLabel =
    note.projectName ?? note.productName ?? note.companyName ?? null;
  const typeColor = TYPE_COLORS[note.type] ?? TYPE_COLORS.general;
  const typeLabel = typeLabels[note.type] ?? note.type;
  const visibleTags = note.tags.slice(0, 3);
  const extraTags = note.tags.length - visibleTags.length;

  return (
    <div
      onClick={onClick}
      className="group flex cursor-pointer flex-col rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: typeColor.bg, color: typeColor.text }}
        >
          {typeLabel}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          title={note.isPinned ? "Unpin" : "Pin"}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
        >
          <Pin
            className="h-[14px] w-[14px]"
            strokeWidth={1.5}
            style={{
              color: note.isPinned ? "hsl(40,95%,50%)" : "hsl(215,16%,65%)",
              fill: note.isPinned ? "hsl(40,95%,55%)" : "none",
            }}
          />
        </button>
      </div>

      <div
        className="mb-1.5 text-[14px] font-semibold leading-snug text-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {note.title}
      </div>

      <div
        className="mb-3 text-[12px] text-muted-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {note.contentPreview.slice(0, 120) || (
          <span className="italic">No content</span>
        )}
      </div>

      {visibleTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {visibleTags.map((t) => (
            <TagBadge key={t} value={t} label={tagLabels[t] ?? t} size="sm" />
          ))}
          {extraTags > 0 && (
            <span className="text-[11px] text-muted-foreground">
              +{extraTags} more
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between text-[11px] text-muted-foreground">
        {contextLabel ? (
          <span
            className="truncate rounded-full px-2 py-0.5"
            style={{ background: "hsl(210,30%,94%)" }}
            title={contextLabel}
          >
            {contextLabel}
          </span>
        ) : (
          <span>{note.code ?? ""}</span>
        )}
        <span className="truncate">
          {note.createdByName ?? "—"} · {formatRelative(note.createdAt)}
        </span>
      </div>
    </div>
  );
}
